import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { API_BASE_URL } from "../utils/config";
import { TeamRoute } from "../components/TeamTag";

function displayOrDash(val) {
  const s = typeof val === "string" ? val.trim() : val;
  return s ? String(s) : "-";
}

export default function AdminBorrowBatches() {
  const [batches, setBatches] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Inline confirm for Reject — replaces window.confirm, which some mobile
  // browsers never dismiss on tap (hasil meet.txt: same "tombol ACC ngestuck"
  // bug as the other ACC flows).
  const [confirmRejectId, setConfirmRejectId] = useState(null);
  const navigate = useNavigate();

  const token = localStorage.getItem("accessToken");

  useEffect(() => {
    async function fetchData() {
      if (!token) {
        setError("You must be logged in as admin to view this page.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const res = await fetch(`${API_BASE_URL}/api/admin/borrow-batches/pending/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to load pending batches");
        const data = await res.json();
        setBatches(Array.isArray(data) ? data : data.results || []);
      } catch (e) {
        console.error(e);
        setError("Failed to load data.");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [token]);

  const printBatch = (id) => {
    navigate(`/borrow-requests/print?batch=${id}`);
  };

  const approve = (id) => {
    navigate(`/admin/borrow-batches/${id}/approval`);
  };

  const editBatch = (id) => {
    navigate(`/borrow-batches/${id}/edit`);
  };

  const reject = async (id) => {
    setConfirmRejectId(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/borrow-batches/${id}/reject/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Reject failed");
      setBatches((prev) => prev.filter((b) => b.id !== id));
    } catch (e) {
      console.error(e);
      window.alert("Reject failed.");
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">Pending Borrow Batches</h1>

      {loading && <p className="text-sm text-muted">Loading…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {!loading && !error && batches.length === 0 && (
        <p className="text-sm text-muted">There are no pending batches.</p>
      )}

      {!loading && !error && batches.length > 0 && (
        <div className="glass-table-wrap overflow-x-auto">
          <table className="glass-table">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">Ref</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">Borrower</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">Event</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">PIC</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">Items</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">Tanggal peminjaman</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">Expected return</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">Status</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">Kelompok</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-muted">Actions</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => {
                const expanded = expandedId === b.id;
                return (
                  <React.Fragment key={b.id}>
                    <tr className="border-t">
                      <td className="px-4 py-2 text-xs font-mono text-strong">{b.reference_code}</td>
                      <td className="px-4 py-2 text-xs text-strong">{b.user}</td>

                      {/* Phase 3 (Display Only): Event + PIC */}
                      <td
                        className="px-4 py-2 text-xs text-strong max-w-[220px] truncate"
                        title={typeof b.event_name === "string" ? b.event_name : ""}
                      >
                        {displayOrDash(b.event_name)}
                      </td>
                      <td
                        className="px-4 py-2 text-xs text-strong max-w-[220px] truncate"
                        title={typeof b.event_pic_name === "string" ? b.event_pic_name : ""}
                      >
                        {displayOrDash(b.event_pic_name)}
                      </td>

                      <td className="px-4 py-2 text-xs">
                        <button
                          type="button"
                          onClick={() => setExpandedId(expanded ? null : b.id)}
                          className="text-brand-700 font-semibold hover:underline"
                        >
                          {expanded ? "Hide" : "Show"} ({b.items?.length || 0})
                        </button>
                      </td>
                      <td className="px-4 py-2 text-xs text-muted">{b.borrow_date || "-"}</td>
                      <td className="px-4 py-2 text-xs text-muted">{b.expected_return_date || "-"}</td>
                      <td className="px-4 py-2 text-xs text-strong">{b.status}</td>
                      <td className="px-4 py-2 text-xs">
                        <TeamRoute
                          requester={b.requester_team_name}
                          approver={b.approver_team_name}
                        />
                      </td>
                      <td className="px-4 py-2 text-right space-x-2">
                        <button
                          onClick={() => printBatch(b.id)}
                          className="px-3 py-1 rounded bg-slate-700 text-white text-xs font-semibold"
                        >
                          Print
                        </button>

                        {b.status === "PENDING" && (
                          <button
                            onClick={() => editBatch(b.id)}
                            className="px-3 py-1 rounded border border-white/50 dark:border-white/15 bg-brand-500/10 text-brand-700 dark:text-brand-300 text-xs font-semibold"
                            title="Edit pending batch"
                          >
                            Edit
                          </button>
                        )}

                        {b.status === "PENDING" && (
                          <button
                            onClick={() => approve(b.id)}
                            className="px-3 py-1 rounded bg-green-600 text-white text-xs font-semibold"
                          >
                            Approve
                          </button>
                        )}

                        <button
                          onClick={() => setConfirmRejectId(b.id)}
                          className="px-3 py-1 rounded bg-red-600 text-white text-xs font-semibold"
                        >
                          Reject
                        </button>
                      </td>
                    </tr>

                    {confirmRejectId === b.id && (
                      <tr className="border-t bg-black/5 dark:bg-white/5">
                        <td colSpan={10} className="px-6 py-3">
                          <div className="flex flex-wrap items-center gap-3">
                            <p className="text-xs flex-1 min-w-[200px]">
                              Tolak batch {b.reference_code}?
                            </p>
                            <button
                              type="button"
                              onClick={() => reject(b.id)}
                              className="px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white text-xs font-semibold"
                            >
                              Ya, tolak
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmRejectId(null)}
                              className="px-3 py-1.5 rounded-md border border-white/50 dark:border-white/15 text-xs font-semibold"
                            >
                              Batal
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}

                    {expanded && (
                      <tr className="border-t bg-brand-500/5">
                        <td colSpan={10} className="px-6 py-3">
                          <div className="text-xs text-strong">
                            {(b.items || []).map((it) => (
                              <div key={it.item} className="flex justify-between py-1 border-b border-white/40 dark:border-white/10 last:border-b-0">
                                <span>{it.item_name || `Item #${it.item}`}</span>
                                <span className="font-semibold">{it.quantity}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}